/**
 * The Test Bench (s3-grading-engine-node24, 1 November 2026) — test an upgraded copy of a function
 * (the CANDIDATE) against the live one, on the same input, before anything in production changes.
 * Server: lambdas/reporting/src/test-bench/ (routes on the reporting service, super_admin only).
 *
 * A run executes in ONE zone — the zone the operator is working in — so it starts there. The run
 * history is a read-only board, so it fans out to every zone and merges (admin UI standards §6);
 * opening a run reads it from the run's own zone.
 */
import { adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

export type Viewports = 'desktop' | 'mobile' | 'both';
export type ViewportKey = 'desktop' | 'mobile';
export type RunStatus = 'running' | 'complete' | 'failed';

export interface CategorySummary { key: string; name: string; score: number; max: number }
export interface ViewportSummary { total: number; grade: string; categories: CategorySummary[]; collected: boolean }

export interface SlotResult {
  slot: 'live' | 'candidate';
  functionName: string;
  runtime: string | null;
  browser: string | null;
  browserSource: 'reported' | 'declared' | null;
  node: string | null;
  pass: boolean;
  durationMs: number;
  error: string | null;
  engineVersion: string | null;
  assessedUrl: string | null;
  requiresAuth: boolean;
  aiCalls: number;
  aiFailedKeys: number;
  viewports: Partial<Record<ViewportKey, ViewportSummary>>;
  notes: string[];
}

export interface Difference { viewport: ViewportKey; what: 'total' | 'grade' | 'category'; key?: string; live: number | string; candidate: number | string }

export interface BenchRun {
  runId: string;
  functionKey: 'grading-engine';
  input: { url: string; viewports: Viewports };
  region: string;
  startedAt: string;
  startedBy: { staffId: string; email: string };
  status: RunStatus;
  finishedAt: string | null;
  live: SlotResult | null;
  candidate: SlotResult | null;
  differences: Difference[];
  error: string | null;
  /** Added by the client after the fan-out. */
  zone?: string;
}

export interface BenchFunction { key: 'grading-engine'; label: string; live: string; candidate: string }

export interface BenchHistory { functions: BenchFunction[]; runs: BenchRun[]; failedZones: string[] }

export async function listBenchHistory(): Promise<BenchHistory> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const r = await adminApiInZone<{ functions: BenchFunction[]; runs: BenchRun[] }>(zone, '/admin/test-bench/runs');
    return [{ functions: r.functions ?? [], runs: (r.runs ?? []).map((run) => ({ ...run, zone: zone.code })) }];
  });
  throwIfAllZonesFailed(results);
  const answered = results.flatMap((r) => r.items);
  return {
    functions: answered[0]?.functions ?? [],
    runs: answered.flatMap((a) => a.runs).sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1)),
    failedZones: failedZoneLabels(results),
  };
}

export async function getBenchRun(zone: PortalZone, runId: string): Promise<BenchRun> {
  const r = await adminApiInZone<{ run: BenchRun }>(zone, `/admin/test-bench/runs/${encodeURIComponent(runId)}`);
  return { ...r.run, zone: zone.code };
}

export async function startBenchRun(zone: PortalZone, input: { function: 'grading-engine'; url: string; viewports: Viewports }): Promise<BenchRun> {
  const r = await adminApiInZone<{ run: BenchRun }>(zone, '/admin/test-bench/runs', { method: 'POST', body: JSON.stringify(input) });
  return { ...r.run, zone: zone.code };
}
